<template>
    <DsModal
        v-if="isExportable"
        :is-open="isOpen"
        show-close
        size="sm"
        class="publish-marketplace-modal"
        :title="$t('content.publishing.package.title')"
        @close="closePublishMarketplace"
    >
        <template #header>
            <DsFilledButton
                id="publishMarketplace-button"
                data-qa="publishMarketplace-button"
                :disabled="disableSave"
                :loading="isLoading"
                @click="publishMarketplace"
            >
                {{ $t('content.publishing.package.export') }}
            </DsFilledButton>
        </template>

        <template #default>
            <div class="listingName-wrapper">
                <DsInputField
                    v-model="listingName"
                    :label="$t('content.publishing.package.listing.name.label')"
                />
                <DsTooltip position="right">
                    <template #reference>
                        <DsIcon name="help-circle" class="help-circle-icon" />
                    </template>

                    <template #default>
                        <div>{{ $t('content.publishing.package.name.tooltip') }}</div>
                    </template>
                </DsTooltip>
            </div>

            <div class="private-wrapper">
                <DsCheckbox
                    v-model="privateCheck"
                    :label="$t('content.publishing.package.privateListing.label')"
                />
                <DsTooltip position="right">
                    <template #reference>
                        <DsIcon name="help-circle" class="help-circle-icon" />
                    </template>

                    <template #default>
                        <div>{{ $t('content.publishing.package.privateListing.tooltip') }}</div>
                    </template>
                </DsTooltip>
            </div>

            <div class="companyName-wrapper">
                <DsMultiselect
                    v-model="selectedCompany"
                    label-prop="label"
                    value-prop="value"
                    bind-value-only
                    :placeholder="$t('content.publishing.package.company.name')"
                    :options="userCompanies"
                    class="selected-company"
                    @input="getListings"
                />
                <DsTooltip position="right">
                    <template #reference>
                        <DsIcon name="help-circle" class="help-circle-icon" />
                    </template>

                    <template #default>
                        <div>{{ $t('content.publishing.package.chooseCompany.tooltip') }}</div>
                    </template>
                </DsTooltip>
            </div>

            <div class="listing-wrapper">
                <DsMultiselect
                    v-model="selectedListing"
                    label-prop="label"
                    value-prop="value"
                    bind-value-only
                    :readonly="!selectedCompany"
                    :add-text="$t('content.publishing.package.listing.create')"
                    :placeholder="$t('content.publishing.package.select.listing')"
                    :options="companyListings"
                />
                <DsTooltip position="right">
                    <template #reference>
                        <DsIcon name="help-circle" class="help-circle-icon" />
                    </template>

                    <template #default>
                        <div>{{ $t('content.publishing.package.createOrUpdateListing.tooltip') }}</div>
                    </template>
                </DsTooltip>
            </div>

            <DsInlineAlert
                v-if="showMessage"
                leading-icon
                :type="typeAlert"
                class="export-alert-message"
            >
                <template #default>
                    <span class="warning-text">{{ messageResult }}</span>
                    <li v-for="(i) in messageWarnings" :key="i">
                        {{ i }}
                    </li>
                </template>
                <template v-if="showLinkButton" #footer>
                    <DsFilledButton
                        gray
                        dense
                        as="a"
                        target="_blank"
                        :href="linkUrl"
                    >
                        <template #default>
                            {{ linkLabel }}
                        </template>
                        <template #trailing>
                            <DsIcon
                                v-if="typeAlert !='critical'"
                                name="external-link"
                            />
                        </template>
                    </DsFilledButton>
                </template>
            </DsInlineAlert>
        </template>
    </DsModal>
    <DsConfirmDialog
        v-else
        :is-open="isOpen"
        :title="$t('content.publishing.package.title')"
        :message="$t('content.publishing.export.disallowed')"
        :confirm-label="$t('content.publishing.package.close')"
        confirm-only
        @confirm="closePublishMarketplace"
    />
</template>

<script>
import {
    isExportable,
    getListingsForCompany,
    loadCompaniesForCurrentUser,
    exportToMarketplace,
} from './publish.marketplace.api';

export default {
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: Number,
            default: 0,
        },
        funnelName: {
            type: String,
            required: true,
        },
    },
    emits: ['close-publish-marketplace', 'save-campaign'],
    data() {
        return {
            listingName: this.funnelName,
            selectedCompany: null,
            selectedListing: null,
            userCompanies: [],
            companyListings: [],
            privateCheck: false,
            isExportable: true,
            messageResult: null,
            showMessage: false,
            isLoading: false,
            linkUrl: '',
            linkLabel: '',
            typeAlert: '',
            messageWarnings: [],
            showLinkButton: true,
        };
    },

    computed: {
        disableSave() {
            return !this.listingName || !this.selectedCompany || !this.selectedListing;
        },
    },

    mounted() {
        this.checkIsExportable();

        if (this.isExportable) {
            this.loadCompanies();
        }
    },

    methods: {
        async loadCompanies() {
            try {
                const data = await loadCompaniesForCurrentUser(this.funnelId, this.funnelName);

                this.userCompanies = data;
            } catch (error) {
                this.$error({ message: this.$t('content.publishing.package.companies.failed') });
            }
        },

        async getListings() {
            try {
                const data = await getListingsForCompany(this.selectedCompany);

                this.companyListings = data;
            } catch (error) {
                this.$error({ message: this.$t('content.publishing.package.listing.failed') });
            }
        },

        async checkIsExportable() {
            try {
                this.isExportable = await isExportable(this.funnelId);
            } catch (error) {
                this.$error({ message: this.$t('content.publishing.package.exportable.failed') });
            }
        },

        async publishMarketplace() {
            try {
                this.isLoading = true;
                this.$emit('save-campaign');
                const data = await exportToMarketplace(this.funnelId, this.listingName, this.privateCheck, this.selectedListing, this.selectedCompany);
                const {
                    actionPerformed,
                    errorMessage,
                    listingEditionViewUrl,
                    warnings,
                } = data;

                if (errorMessage) {
                    if (errorMessage.includes('legacy email')) {
                        this.showLinkButton = false;
                        this.messageResult = this.$t('content.publishing.package.publish.failed.legacyEmail');
                        this.typeAlert = 'critical';
                    } else {
                        this.messageResult = this.$t('content.publishing.package.publish.failed');
                        this.linkUrl = this.$t('content.publishing.package.publish.contact.support');
                        this.linkLabel = this.$t('content.publishing.package.publish.error.link');
                        this.typeAlert = 'critical';
                    }
                } else if (warnings.length > 0) {
                    if (actionPerformed === 'export.existingListing') {
                        this.messageResult = this.$t('content.publishing.package.listing.update.warnings').replace('listingName', this.listingName);
                    } else {
                        this.messageResult = this.$t('content.publishing.package.listing.create.warnings').replace('listingName', this.listingName);
                    }
                    this.linkUrl = listingEditionViewUrl;
                    this.linkLabel = this.$t('content.publishing.export.listing.link');
                    this.typeAlert = 'warning';
                    this.messageWarnings = warnings;
                } else {
                    if (actionPerformed === 'export.newListing') {
                        this.messageResult = this.$t('content.publishing.package.listing.create.success').replace('listingName', this.listingName);
                    } else {
                        this.messageResult = this.$t('content.publishing.package.listing.update.success').replace('listingName', this.listingName);
                    }

                    this.linkUrl = listingEditionViewUrl;
                    this.linkLabel = this.$t('content.publishing.export.listing.link');
                    this.typeAlert = 'good';
                    await this.getListings();
                }
                this.showMessage = true;
                this.isLoading = false;
            } catch (error) {
                this.$error({ message: this.$t('content.publishing.package.publish.failed') });
                this.closePublishMarketplace();
            }
        },

        closePublishMarketplace() {
            this.$emit('close-publish-marketplace');
        },
    },
};
</script>

<style scoped>
    .listingName-wrapper,
    .private-wrapper,
    .companyName-wrapper,
    .listing-wrapper {
        display: inline-flex;
        align-items: center;
        margin-bottom: var(--input-margin-bottom, 1.5rem);
    }

    div.listingName-wrapper > div,
    div.companyName-wrapper > div,
    div.listing-wrapper > div {
        margin-right: 15px;
        margin-bottom: 0;
    }

    .help-circle-icon {
        padding: 2px;
        color: #666666;
    }

    div > div.private-wrapper > label {
        padding: 0;
        margin-right: 15px;
    }

    #publishMarketplace-button.loading {
        display: block !important;
    }

</style>

<style lang="scss">
.publish-marketplace-modal {
    div.modal-body {
        padding: 0;
    }
}
div.companyName-wrapper > div > div > div.dropdown-reference > div > div.multiselect-tags > span > label,
div.listing-wrapper > div > div > div.dropdown-reference > div > div.multiselect-tags > span > label {
    padding-top: 0;
}

.inline-alert.export-alert-message.good > span,
.inline-alert.export-alert-message.warning > span,
.inline-alert.export-alert-message.critical > span {
    font-size: $font-size-sm;
    line-height: normal;
}
</style>
